import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, Shield, CheckCircle2, Lock, FileText, Wrench, Share2 } from "lucide-react";

/**
 * PreCheckoutConsentModal — shown right before redirecting to Stripe Checkout.
 *
 * UK Consumer Contracts Regulations 2013, Regulation 36 — for "digital content"
 * (downloadable or accessed once purchased) the seller MUST obtain the user's
 * EXPLICIT consent that performance begins immediately AND acknowledgement that
 * the 14-day cancellation right is lost. Without this, Stripe / chargeback
 * disputes default to refunding the customer.
 *
 * The component:
 *   - Surfaces a single, blunt warning paragraph
 *   - Requires the user to tick a checkbox before the £1 / £4.99 CTA enables
 *   - Records `consumer_consent_waived: true` in the backend transaction
 */
export const PreCheckoutConsentModal = ({
  open,
  onClose,
  onConfirm,
  amount,          // 1.00 or 4.99
  purpose,         // 'verdict' or 'pdf'
  registration,
  t,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shakeConsent, setShakeConsent] = useState(false);
  const consentLabelRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      setAccepted(false);
      setSubmitting(false);
      setShakeConsent(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (submitting) return;
    // Tap-on-disabled → guide the user to the consent box instead of silently
    // failing. Shake it, scroll it into view, and bail. This eliminates the
    // "I tapped Pay and nothing happened" UX seen on mobile.
    if (!accepted) {
      setShakeConsent(true);
      setTimeout(() => setShakeConsent(false), 600);
      try {
        consentLabelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* older browsers */ }
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ consumer_consent_waived: true });
    } catch (_e) {
      setSubmitting(false);
    }
  };

  const isPdf = purpose === "pdf";
  const productName = isPdf
    ? (t("preCheckoutPdfProductName") || "Premium Worthify Report (PDF + 15 modules)")
    : (t("preCheckoutVerdictProductName") || "Worthify Basic Verdict");

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            data-testid="precheckout-backdrop"
            style={{
              position: "fixed", inset: 0, zIndex: 70,
              background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
            }}
          />
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="fixed left-0 right-0 bottom-0 z-[71] mx-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="precheckout-title"
            aria-describedby="precheckout-warning-body"
            style={{
              maxWidth: 460,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              background: "white",
              padding: "20px 18px 24px",
              boxShadow: "0 -10px 30px rgba(15,23,42,0.18)",
            }}
            data-testid="precheckout-consent-modal"
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              data-testid="precheckout-close"
              aria-label="Close"
              className="absolute"
              style={{
                top: 12, right: 12, width: 30, height: 30, borderRadius: 10,
                background: "#F1F5F9", color: "rgba(15,23,42,0.6)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X className="w-4 h-4" strokeWidth={2.4} />
            </button>

            {/* Drag handle */}
            <div
              aria-hidden
              className="mx-auto mb-3"
              style={{ width: 38, height: 4, borderRadius: 2, background: "#E2E8F0" }}
            />

            {/* Header */}
            <div className="flex items-start gap-3 pr-8">
              <motion.div
                initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 340, damping: 18, delay: 0.05 }}
                className="precheckout-shield flex items-center justify-center flex-shrink-0"
                style={{
                  width: 44, height: 44, borderRadius: 14,
                  background: "linear-gradient(135deg,#0F172A,#1E293B)",
                  color: "var(--wf-modal-text)",
                  boxShadow: "0 6px 14px rgba(15,23,42,0.25)",
                }}
              >
                <Shield className="w-5 h-5" strokeWidth={2.4} />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.12, duration: 0.4 }}
                className="flex-1 min-w-0"
              >
                <p
                  className="uppercase font-bold"
                  style={{ fontSize: 10.5, letterSpacing: "0.22em", color: "rgba(15,23,42,0.5)" }}
                >
                  {t("preCheckoutBadge") || "Confirm purchase"}
                </p>
                <p
                  className="font-black mt-0.5 leading-[1.2]"
                  id="precheckout-title"
                  style={{ fontSize: 18, color: "#0F172A", letterSpacing: "-0.4px" }}
                >
                  {productName}
                </p>
                {registration && (
                  <motion.p
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 380, damping: 16, delay: 0.25 }}
                    className="font-mono font-bold mt-1"
                    style={{
                      fontSize: 11.5,
                      color: "#0F172A",
                      background: "#FEF08A",
                      padding: "2px 8px",
                      borderRadius: 6,
                      width: "fit-content",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {registration.toUpperCase()}
                  </motion.p>
                )}
              </motion.div>
            </div>

            {/* Benefits — surface what the user gets BEFORE the legal small-print.
                Only shown for the £4.99 PDF/premium tier (verdict tier already
                got pitched on the verdict screen). */}
            {isPdf && (
              <>
                {/* "More Than HPI Check" positioning kicker */}
                <div
                  className="mt-3 flex items-center justify-center gap-1.5"
                  data-testid="precheckout-hpi-positioning"
                >
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block", width: 6, height: 6, borderRadius: "50%",
                      background: "linear-gradient(135deg,#34A853 0%,#16A34A 100%)",
                    }}
                  />
                  <span
                    className="uppercase font-black text-center"
                    style={{ fontSize: 9.5, letterSpacing: "0.18em", color: "#16A34A" }}
                  >
                    {t("premiumPositioningTagline") || "More Than HPI Check"}
                  </span>
                </div>
                <p
                  className="text-center leading-[1.35] mt-1"
                  style={{ fontSize: 10.5, color: "rgba(15,23,42,0.55)", fontWeight: 600 }}
                >
                  {t("premiumPositioningSub") || "Finance · Stolen · Write-Off · VIN · AI Insights · Market Valuation"}
                </p>

                <div
                  className="mt-3 grid grid-cols-2 gap-2"
                  data-testid="precheckout-benefits"
                >
                {[
                  { icon: FileText, label: t("preCheckoutBenefitValuation") || "Market valuation" },
                  { icon: Shield, label: t("preCheckoutBenefitRecall") || "Recall + write-off check" },
                  { icon: Wrench, label: t("preCheckoutBenefitMot") || "MOT risk analysis" },
                  { icon: Share2, label: t("preCheckoutBenefitShare") || "PDF + share with mechanic" },
                ].map(({ icon: Icon, label }, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      delay: 0.32 + i * 0.07,
                      duration: 0.4,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={{ y: -2, transition: { duration: 0.18 } }}
                    className="flex items-start gap-2"
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#16A34A" }} strokeWidth={2.6} />
                    <span style={{ fontSize: 11, color: "rgba(15,23,42,0.78)", fontWeight: 600, lineHeight: 1.25 }}>
                      {label}
                    </span>
                  </motion.div>
                ))}
              </div>
              </>
            )}

            {/* Price */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="precheckout-price-card mt-4 flex items-center justify-between"
              style={{
                padding: "12px 14px",
                borderRadius: 14,
                background: "linear-gradient(135deg,#F0FDF4 0%,#DCFCE7 100%)",
                border: "1px solid #86EFAC",
              }}
            >
              <div>
                <p
                  className="uppercase font-bold"
                  style={{ fontSize: 10, letterSpacing: "0.2em", color: "rgba(20,83,45,0.7)" }}
                >
                  {t("preCheckoutTotal") || "Total"}
                </p>
                <motion.p
                  key={`price-${amount}`}
                  initial={{ scale: 0.7 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 360, damping: 18, delay: 0.6 }}
                  className="font-black mt-0.5"
                  style={{ fontSize: 24, color: "#14532D", letterSpacing: "-0.5px" }}
                >
                  £{amount.toFixed(2)}
                </motion.p>
              </div>
              <div className="text-right">
                <p style={{ fontSize: 10.5, color: "rgba(20,83,45,0.75)" }}>
                  {t("preCheckoutOneTime") || "One-time payment"}
                </p>
                <p
                  className="font-bold mt-0.5"
                  style={{ fontSize: 11, color: "#14532D" }}
                >
                  {t("preCheckoutNoSub") || "No subscription · No auto-renewal"}
                </p>
              </div>
            </motion.div>

            {/* Legal warning */}
            <div
              className="mt-4 p-3.5 flex items-start gap-2.5"
              data-testid="precheckout-warning"
              style={{
                borderRadius: 14,
                background: "#FFFBEB",
                border: "1.5px solid #FDE68A",
              }}
            >
              <AlertTriangle
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: "#B45309" }}
                strokeWidth={2.4}
              />
              <div>
                <p
                  className="font-black"
                  style={{ fontSize: 13, color: "#78350F", lineHeight: 1.25 }}
                >
                  {t("preCheckoutWarningTitle") || "Instant digital delivery"}
                </p>
                <p
                  className="mt-1 leading-[1.45]"
                  id="precheckout-warning-body"
                  style={{ fontSize: 12, color: "rgba(120,53,15,0.92)" }}
                >
                  {t("preCheckoutWarningBody") ||
                    "Your report is delivered immediately in your browser the moment payment succeeds. By confirming, you agree to start performance now and waive your 14-day cancellation right under the UK Consumer Contracts Regulations 2013."}
                </p>
              </div>
            </div>

            {/* Consent checkbox */}
            <motion.label
              ref={consentLabelRef}
              animate={shakeConsent ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
              transition={{ duration: 0.55 }}
              className="mt-3.5 flex items-start gap-2.5 cursor-pointer select-none"
              style={{
                padding: "12px 14px",
                borderRadius: 14,
                background: accepted ? "#F0FDF4" : shakeConsent ? "#FEF3C7" : "#F8FAFC",
                border: accepted
                  ? "1.5px solid #86EFAC"
                  : shakeConsent
                    ? "1.5px solid #F59E0B"
                    : "1.5px solid #E2E8F0",
                transition: "background 180ms, border-color 180ms",
              }}
              data-testid="precheckout-consent-label"
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="precheckout-consent-checkbox"
                className="sr-only"
              />
              <span
                className="flex-shrink-0 flex items-center justify-center"
                style={{
                  width: 22, height: 22, borderRadius: 7,
                  background: accepted ? "linear-gradient(135deg,#22C55E,#16A34A)" : "white",
                  border: accepted ? "none" : "2px solid #CBD5E1",
                  transition: "all 180ms",
                  marginTop: 1,
                }}
              >
                {accepted && <CheckCircle2 className="w-3.5 h-3.5 text-slate-900 dark:text-white" strokeWidth={3} />}
              </span>
              <span
                className="leading-[1.4]"
                style={{ fontSize: 12.5, color: accepted ? "#14532D" : "rgba(15,23,42,0.78)" }}
              >
                {t("preCheckoutConsent") ||
                  "I agree to instant delivery and understand I lose my 14-day cancellation right."}
              </span>
            </motion.label>

            {/* CTA — tap-when-disabled triggers shake+scroll to consent box instead of silent fail */}
            <motion.button
              type="button"
              whileTap={{ scale: 0.97 }}
              onClick={handleConfirm}
              aria-disabled={!accepted || submitting}
              data-testid="precheckout-confirm-btn"
              className="w-full mt-4 flex items-center justify-center gap-2 font-bold"
              style={{
                padding: "14px 18px",
                borderRadius: 14,
                background: accepted && !submitting
                  ? "linear-gradient(135deg,#0F172A 0%,#1E293B 100%)"
                  : "linear-gradient(135deg,#94A3B8 0%,#64748B 100%)",
                color: "var(--wf-modal-text)",
                fontSize: 14,
                boxShadow: accepted && !submitting ? "0 8px 22px rgba(15,23,42,0.32)" : "none",
                opacity: accepted && !submitting ? 1 : 0.78,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              <Lock className="w-4 h-4" strokeWidth={2.6} />
              {submitting
                ? (t("preCheckoutRedirecting") || "Redirecting to Stripe…")
                : !accepted
                  ? (t("preCheckoutTickToContinue") || "Tick the box to continue")
                  : (t("preCheckoutPayCta") || "Pay £{n} with Stripe").replace("{n}", amount.toFixed(2))}
            </motion.button>

            {/* Trust line */}
            <p
              className="mt-3 text-center leading-[1.4]"
              style={{ fontSize: 10.5, color: "rgba(15,23,42,0.5)" }}
            >
              {t("preCheckoutTrustLine") ||
                "Secured by Stripe · Card details never touch Worthify · Receipt sent automatically"}
            </p>

            {/* Legal links */}
            <div
              className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1"
              style={{ fontSize: 10.5 }}
            >
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "rgba(15,23,42,0.55)", textDecoration: "underline", textUnderlineOffset: 2 }}
                data-testid="precheckout-link-terms"
              >
                {t("legalNavTerms") || "Terms"}
              </a>
              <span style={{ color: "rgba(15,23,42,0.3)" }}>·</span>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "rgba(15,23,42,0.55)", textDecoration: "underline", textUnderlineOffset: 2 }}
                data-testid="precheckout-link-privacy"
              >
                {t("legalNavPrivacy") || "Privacy"}
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
